import { useCallback, useMemo, useState } from 'react';
import { ConfirmationPopup } from '../../../../components';
import {
  useFulfillmentAddHandler,
  useFulfillmentBulkGeneratePalletsHandler,
} from '../../../../contexts/fulfillment-tab-actions';
import { PalletService, ToastMessage } from '../../../../utility';
import { PalletListWarehouseHook } from '../../../../hooks';
import { AddPalletPopup, PalletsTable } from '../../../../page-components/settings';
import { PalletResetPopup } from '../../../../page-components/pallet/PalletResetPopup';
import { GeneratePalletLabelPopup } from '@page-components/pallet/GenerateLabelPopup';
import { BulkGeneratePalletsModal } from '@page-components/pallet/BulkGeneratePalletsModal';
import { BulkGeneratePalletLabelsModal } from '@page-components/pallet/BulkGeneratePalletLabelsModal';
import { AlertFooterBar } from '../../../../elements/FooterAlert';

export function PalletsTab({ warehouseId, search }) {
  const {
    data,
    totalData,
    loading,
    filter,
    filterChanged,
    pageChanged,
    refresh,
    sort,
    onSortChange,
  } = PalletListWarehouseHook(warehouseId, null, true, search);

  const [addRulePopup, setAddRulePopup] = useState(false);
  const [deletePalletPopup, setDeletePopup] = useState(null);
  const [generateLabelPopupOpen, setGenerateLabelPopupOpen] = useState(false);
  const [palletNumberForLabel, setPalletNumberForLabel] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [palletToReset, setPalletToReset] = useState(null);
  const [bulkGeneratePalletsOpen, setBulkGeneratePalletsOpen] = useState(false);
  const [bulkGenerateLabelsOpen, setBulkGenerateLabelsOpen] = useState(false);
  const [selectedById, setSelectedById] = useState({});

  const selectedItems = useMemo(() => Object.values(selectedById), [selectedById]);
  const selectedCount = selectedItems.length;
  const selectedPalletNumbers = useMemo(
    () => selectedItems.map(item => item.palletNumber).filter(Boolean),
    [selectedItems],
  );

  const openGenerateLabelPopup = palletNumber => {
    setGenerateLabelPopupOpen(true);
    setPalletNumberForLabel(palletNumber);
  };

  const onCloseGenerateLabelPopup = () => {
    setGenerateLabelPopupOpen(false);
    setPalletNumberForLabel(null);
  };

  const onSelectRow = useCallback((item, selected) => {
    setSelectedById(prev => {
      const next = { ...prev };
      if (selected) {
        next[item._id] = item;
      } else {
        delete next[item._id];
      }
      return next;
    });
  }, []);

  const onSelectAllRows = useCallback((items, selected) => {
    setSelectedById(prev => {
      const next = { ...prev };
      items.forEach(item => {
        if (selected) {
          next[item._id] = item;
        } else {
          delete next[item._id];
        }
      });
      return next;
    });
  }, []);

  const clearBulkSelection = useCallback(() => {
    setSelectedById({});
  }, []);

  const handleBulkLabelsCompleted = useCallback(() => {
    setSelectedById({});
    refresh();
  }, [refresh]);

  const openBulkGeneratePallets = useCallback(() => {
    setBulkGeneratePalletsOpen(true);
  }, []);

  const openPopup = useCallback(() => {
    setAddRulePopup(true);
  }, []);

  useFulfillmentAddHandler(openPopup);
  useFulfillmentBulkGeneratePalletsHandler(openBulkGeneratePallets);

  const closePopup = () => {
    setAddRulePopup(false);
  };

  const editPallet = pallet => {
    setAddRulePopup(pallet);
  };

  const onDeletePallet = pallet => {
    setDeletePopup(pallet);
  };

  const onCloseDelete = () => {
    setDeletePopup(null);
  };

  const deletePallet = async () => {
    try {
      setProcessing(true);

      await PalletService.remove(deletePalletPopup._id);
      ToastMessage.success(`${deletePalletPopup.palletNumber} deleted successfully.`);
      setDeletePopup(null);
      refresh();
    } catch (error) {
      ToastMessage.apiError(error?.error || error);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className={selectedCount > 0 ? 'pb-4 mb-4' : ''}>
      <PalletsTable
        data={data || []}
        loading={loading}
        totalData={totalData}
        filter={filter}
        sort={sort}
        pageChanged={pageChanged}
        filterChanged={filterChanged}
        onSortChange={onSortChange}
        selectedById={selectedById}
        onSelectRow={onSelectRow}
        onSelectAllRows={onSelectAllRows}
        editPallet={editPallet}
        onDeletePallet={onDeletePallet}
        resetPallet={pallet => setPalletToReset(pallet)}
      />

      <AddPalletPopup
        onClose={closePopup}
        open={addRulePopup}
        refresh={refresh}
        warehouseId={warehouseId}
        item={addRulePopup}
        openGenerateLabelPopup={openGenerateLabelPopup}
      />

      <ConfirmationPopup
        onConfirm={deletePallet}
        onClose={onCloseDelete}
        open={deletePalletPopup}
        loading={processing}
        title="Delete Pallet"
        message={`Are you sure to delete ${deletePalletPopup?.palletNumber}`}
      />

      <PalletResetPopup
        open={!!palletToReset}
        pallet={palletToReset}
        refresh={refresh}
        onClose={() => setPalletToReset(null)}
      />

      <GeneratePalletLabelPopup
        warehouseId={warehouseId}
        open={generateLabelPopupOpen}
        onClose={onCloseGenerateLabelPopup}
        palletNumber={palletNumberForLabel}
      />

      <BulkGeneratePalletsModal
        open={bulkGeneratePalletsOpen}
        onClose={() => setBulkGeneratePalletsOpen(false)}
        warehouseId={warehouseId}
        onCompleted={refresh}
      />

      <BulkGeneratePalletLabelsModal
        open={bulkGenerateLabelsOpen}
        onClose={() => setBulkGenerateLabelsOpen(false)}
        warehouseId={warehouseId}
        palletNumbers={selectedPalletNumbers}
        onCompleted={handleBulkLabelsCompleted}
      />

      {selectedCount > 0 ? (
        <AlertFooterBar
          message={
            selectedCount === 1
              ? '1 pallet selected'
              : `${selectedCount} pallets selected`
          }
          cancel={clearBulkSelection}
          cancelButtonText="Clear"
          save={() => setBulkGenerateLabelsOpen(true)}
          saveButtonText="Bulk Generate Labels"
        />
      ) : null}
    </div>
  );
}
